<template>
  <ModalComponent
    class="cancel-review-modal"
    :submitButtonText="t('dashboard.portfolio.footer.confirm_cancel_btn')"
    :hideSubmitButton="true"
    @setForceToggle="setForceToggle"
    @close="close"
    size="xl"
  >
    <template #title>
      {{ t("dashboard.portfolio.footer.review_cancel_title") }}
    </template>

    <template #body>
      <!-- Vertical padding only: the modal-body already supplies the 1rem
           horizontal inset, so content lines up with the header title and the
           footer buttons instead of being indented an extra step. -->
      <div class="py-3">
        <RegionMismatchAdvisory v-if="hasRegionMismatch" class="mb-3" />

        <div v-if="hasInsufficientInventory" class="error-note mb-3">
          <FontAwesomeIcon icon="exclamation-triangle" class="flex-shrink-0" />
          <span class="fw-semibold">{{
            t("dashboard.portfolio.footer.risk_insufficient_inventory")
          }}</span>
        </div>

        <!-- Shared settings · eyebrow kicker + clean bordered panel, mirroring
             the editor's "Shared Settings" sub-section typography. -->
        <div class="eyebrow-label mb-2">
          {{ t("go_cancel.shared_defaults_title") }}
        </div>
        <div class="border rounded-3 p-3 mb-4">
          <div class="row g-3">
            <div class="col-sm-4">
              <div class="text-secondary small mb-1">
                {{ t("go_cancel.form.beneficiary_type.label") }}
              </div>
              <div class="fw-medium">
                {{
                  t(
                    `go_cancel.form.beneficiary_type.${sharedDefaults.beneficiary_type}`
                  )
                }}
              </div>
            </div>
            <div class="col-sm-4">
              <div class="text-secondary small mb-1">
                {{ t("go_cancel.form.period_start.label") }}
              </div>
              <div class="fw-medium">{{ sharedDefaults.period_start }}</div>
            </div>
            <div class="col-sm-4">
              <div class="text-secondary small mb-1">
                {{ t("go_cancel.form.period_end.label") }}
              </div>
              <div class="fw-medium">{{ sharedDefaults.period_end }}</div>
            </div>
          </div>

          <div class="mt-3 pt-3 border-top">
            <div class="text-secondary small mb-1">
              {{ t("go_cancel.form.global_operation_notes.label") }}
            </div>
            <div class="fw-medium">{{ globalOperationNotes || "-" }}</div>
          </div>
        </div>

        <!-- Cancellation statements · eyebrow kicker + a border-rounded
             DataTable. `border rounded` triggers the app-wide
             `.pv-datatable.rounded { overflow: hidden }` clip so the last row's
             corners no longer poke past the rounded border. -->
        <div class="eyebrow-label mb-2">
          {{ t("go_cancel.statements_title") }}
        </div>
        <TruncatedTextReveal ref="textReveal" class="review-table">
          <DataTable
            :value="materializedStatements"
            dataKey="temp_id"
            class="p-datatable-sm border rounded"
            scrollable
            scrollHeight="min(460px, 50vh)"
            :virtualScrollerOptions="
              materializedStatements.length > 10
                ? virtualScrollerOptions
                : undefined
            "
          >
            <Column :header="t('go_cancel.form.beneficiary_name.label')">
              <template #body="{ data }">
                <span class="review-text text-truncate" tabindex="0">{{
                  data.beneficiary_name
                }}</span>
              </template>
            </Column>
            <Column :header="t('go_cancel.form.registry_country_region.label')">
              <template #body="{ data }">
                <div class="review-registry">
                  <span class="review-text text-truncate" tabindex="0">
                    {{ getRegistryLabel(data.registry_country_region, t) }}
                  </span>
                  <PrivateStatementPill
                    v-if="isStatementPrivate(data.grexel_options)"
                  />
                </div>
              </template>
            </Column>
            <Column
              :header="t('go_cancel.form.registry_code.short_label')"
              style="width: 130px"
            >
              <template #body="{ data }">
                <span class="review-text text-truncate" tabindex="0">{{
                  data.registry_code || "-"
                }}</span>
              </template>
            </Column>
            <Column :header="t('go_cancel.form.address.label')">
              <template #body="{ data }">
                <span class="review-text text-truncate" tabindex="0">{{
                  data.address || "-"
                }}</span>
              </template>
            </Column>
            <Column header="MWh" style="width: 80px">
              <template #body="{ data }">
                {{ data.mwh_cancelled }}
              </template>
            </Column>
            <Column
              :header="t('go_cancel.form.cancellation_purpose_text.label')"
              style="min-width: 220px"
            >
              <template #body="{ data }">
                <span class="review-text text-truncate" tabindex="0">{{
                  data.cancellation_purpose || "-"
                }}</span>
              </template>
            </Column>
          </DataTable>
        </TruncatedTextReveal>
      </div>
    </template>

    <template #footer>
      <button type="button" class="btn-ghost" @click="close">
        {{ t("common.back") }}
      </button>
      <button
        type="button"
        class="btn btn-danger"
        :disabled="confirmDisabled || isLoading"
        @click="confirm"
      >
        <span
          v-if="isLoading"
          class="spinner-border spinner-border-sm me-2"
          role="status"
          aria-hidden="true"
        ></span>
        {{ t("dashboard.portfolio.footer.confirm_cancel_btn") }}
      </button>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import TruncatedTextReveal from "@/components/common/TruncatedTextReveal.vue";
import PrivateStatementPill from "@/dashboard/inventory_management/cancel/PrivateStatementPill.vue";
import RegionMismatchAdvisory from "@/dashboard/inventory_management/cancel/RegionMismatchAdvisory.vue";
import {
  type CancelSharedDefaults,
  isStatementPrivate,
} from "@/dashboard/inventory_management/common/cancelStatementHelpers";
import type { IndividualCancelStatement } from "@/types";
import { getRegistryLabel } from "@/utils/registries";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import { ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

defineProps<{
  hasRegionMismatch: boolean;
  hasInsufficientInventory: boolean;
  isLoading: boolean;
  confirmDisabled: boolean;
  materializedStatements: IndividualCancelStatement[];
  sharedDefaults: CancelSharedDefaults;
  globalOperationNotes: string | null;
}>();

const emit = defineEmits<{
  confirm: [];
}>();

const { t } = useI18n();
// PrimeVue positions virtual rows using itemSize, so content must not change row height.
const ROW_HEIGHT = 48;
const virtualScrollerOptions = { itemSize: ROW_HEIGHT, numToleratedItems: 5 };
const textReveal = useTemplateRef("textReveal");

const forceToggle = ref<((value: boolean) => void) | null>(null);

const setForceToggle = (fn: (value: boolean) => void) => {
  forceToggle.value = fn;
};

const open = () => {
  forceToggle.value?.(true);
};

const close = () => {
  textReveal.value?.hide();
  forceToggle.value?.(false);
};

const confirm = () => {
  close();
  emit("confirm");
};

defineExpose({ open, close });
</script>

<style scoped lang="scss">
.cancel-review-modal {
  :deep(.modal-content) {
    max-height: calc(100dvh - 3.5rem);
  }

  :deep(.modal-body) {
    overflow-y: auto;
  }
}

.review-table {
  :deep(.pv-datatable-container) {
    overflow: auto;
  }

  :deep(.pv-datatable-table) {
    min-width: 900px;
  }

  :deep(.pv-datatable-body-cell) {
    height: v-bind("`${ROW_HEIGHT}px`");
    padding-block: 3px;
    overflow: hidden;
  }
}

.review-text {
  display: block;

  &:focus-visible {
    outline: 2px solid var(--phoenix-primary);
    outline-offset: -2px;
  }
}

.review-registry {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow: hidden;

  .review-text {
    width: 100%;
  }
}
</style>
